import React, { useState } from "react";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";

import { resetPassword } from "../../Actions/AuthActions";
import { MyArrowBackIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

// Helper Functions
const cleanEmail = (value) => {
	return value
		.replace(/[\u200B-\u200D\uFEFF]/g, "")
		.replace(/\s+/g, "")
		.trim();
};

// Loading Spinner
const LoadingSpinner = () => (
	<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
);

// Validation Schema
const recoveryEmailSchema = yup.object().shape({
	email: yup
		.string()
		.trim()
		.required(VALIDATION.REQUIRED_FIELD("Email"))
		.min(1, VALIDATION.MIN_LENGTH("Email", 1))
		.max(70, VALIDATION.MAX_LENGTH("Email", 70))
		.email(VALIDATION.INVALID_EMAIL)
		.matches(
			/^[a-zA-Z0-9.@_\-]+$/,
			VALIDATION.INVALID_EMAIL
		),
});

const ForgotPassword = ({
	setEmail,
	setAccessToken,
	ip,
	showSnackbar,
	setIsResetPassword,
	setCurrentScreen,
	SCREENS,
}) => {
	const [showCheckEmailScreen, setShowCheckEmailScreen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(false);

	const recoveryEmailFormik = useFormik({
		initialValues: { email: "" },
		validationSchema: recoveryEmailSchema,
		onSubmit: handleForgotPassword,
	});

	async function handleForgotPassword() {
		setButtonDisable(true);
		setLoading(true);

		const userEmail = recoveryEmailFormik.values.email;
		setEmail(userEmail);

		try {
			const data = { email: userEmail, ip };
			const res = await resetPassword(data);

			if (res.status === "202") {
				showSnackbar(res.message, "info");
			} else {
				setIsResetPassword(true);
				setShowCheckEmailScreen(true);
				setAccessToken(res.data?.data);
				showSnackbar(res.data.message, "success");
			}
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setLoading(false);
			setButtonDisable(false);
		}
	}

	const getButtonDisabled = () => {
		return buttonDisable || loading || !recoveryEmailFormik.values.email || !!recoveryEmailFormik.errors.email;
	};

	const handleBackToLogin = () => {
		recoveryEmailFormik.resetForm();
		setShowCheckEmailScreen(false);
		setCurrentScreen(SCREENS.LOGIN);
	};

	if (showCheckEmailScreen) {
		return (
			<>
				<div className="mb-6 p-4 text-center bg-light rounded-3">
					<p className="text-muted mb-0 fs-6">
						Didn't receive the email? Check your spam folder or try again.
					</p>
				</div>

				<div className="d-grid mb-4">
					<button
						className="btn btn-outline fs-5 h-50px"
						onClick={() => {
							setShowCheckEmailScreen(false);
							recoveryEmailFormik.setFieldValue("email", "");
						}}
					>
						Try Different Email
					</button>
				</div>

				<div className="d-grid">
					<button
						className="btn btn-primary fs-5 h-50px"
						onClick={() => {
							setShowCheckEmailScreen(false);
							setCurrentScreen(SCREENS.VERIFY_OTP);
						}}
					>
						I Got the OTP - Verify Now
					</button>
				</div>

				<div className="text-center mt-6">
                    <button
                        type="button"
                        onClick={handleBackToLogin}
                        className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
                	>
                        <MyArrowBackIcon className="back-arrow me-2" />
					<span>Back to Login</span>    
                    </button>
				</div>
			</>
		);
	}
	return (
		<>
			<div className="fv-row mb-6 fv-plugins-icon-container">
				<TextField
					type="text"
					placeholder="Enter your email address"
					name="email"
					autoComplete="off"
					className="form-control fs-5 bg-transparent"
					value={recoveryEmailFormik.values.email}
					onChange={(e) => recoveryEmailFormik.setFieldValue("email", cleanEmail(e.target.value))}
					onPaste={(e) => {
						e.preventDefault();
						recoveryEmailFormik.setFieldValue("email", cleanEmail(e.clipboardData.getData("text")));
					}}
					onBlur={recoveryEmailFormik.handleBlur}
					error={recoveryEmailFormik.touched.email && Boolean(recoveryEmailFormik.errors.email)}
					helperText={recoveryEmailFormik.touched.email && recoveryEmailFormik.errors.email}
				/>
			</div>

			<div className="d-grid">
				<button
					onClick={recoveryEmailFormik.handleSubmit}
					className="btn btn-primary fs-3 h-50px"
					disabled={getButtonDisabled()}
				>
					Send Reset Link
					{loading && <LoadingSpinner />}
				</button>
			</div>

        <div className="text-center mt-6">
            <button
                type="button"
                onClick={handleBackToLogin}
                className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
            >
                <MyArrowBackIcon className="back-arrow me-2" />
                <span>Back to Login</span>
            </button>
        </div>
		</>
	);
};

export default ForgotPassword;